import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Clock, User, Calendar, Tag, ArrowRight } from "lucide-react";
import { blogPosts } from "../data/blogPosts";

export default function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);
  const related = post
    ? [
        ...blogPosts.filter(p => p.slug !== slug && p.category === post.category),
        ...blogPosts.filter(p => p.slug !== slug && p.category !== post.category),
      ].slice(0, 3)
    : [];

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h1 className="font-teko text-6xl text-white uppercase">Post Not Found</h1>
          <Link to="/blog" className="ninja-btn-primary mt-6 inline-block font-teko tracking-widest">BACK TO BLOG</Link>
        </div>
      </div>
    );
  }

  const blogPostingSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.metaDescription,
    "image": post.image,
    "author": { "@type": "Organization", "name": "Ninja Screen Print" },
    "publisher": {
      "@type": "Organization",
      "name": "Ninja Screen Print",
      "logo": { "@type": "ImageObject", "url": "https://ninjascreenprint.com/logo.png" }
    },
    "datePublished": post.date,
    "dateModified": post.date,
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://ninjascreenprint.com/blog/${post.slug}` },
    "keywords": post.tags.join(", ")
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ninjascreenprint.com" },
      { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://ninjascreenprint.com/blog" },
      { "@type": "ListItem", "position": 3, "name": post.title, "item": `https://ninjascreenprint.com/blog/${post.slug}` }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{`${post.title} | Ninja Screen Print Dallas TX`}</title>
        <meta name="description" content={post.metaDescription} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:image" content={post.image} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(blogPostingSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="pt-16">
        {/* Hero Image */}
        <div className="relative h-64 sm:h-80 lg:h-96 overflow-hidden">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 ninja-container pb-8">
            <div className="flex items-center gap-2 mb-3">
              <Link to="/blog" className="flex items-center gap-1 text-xs text-neutral-400 hover:text-white transition-colors font-inter">
                <ArrowLeft size={12} /> Blog
              </Link>
              <span className="text-neutral-700">/</span>
              <span className="text-xs text-red-500 font-inter uppercase tracking-widest">{post.category}</span>
            </div>
            <h1 className="font-teko text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight max-w-4xl leading-tight">{post.title}</h1>
          </div>
        </div>

        <div className="ninja-container py-8">
          {/* Meta */}
          <div className="flex flex-wrap gap-4 text-xs text-neutral-500 font-inter mb-8 pb-6 border-b border-neutral-800">
            <span className="flex items-center gap-1"><User size={10} /> {post.author}</span>
            <span className="flex items-center gap-1"><Calendar size={10} /> {post.date}</span>
            <span className="flex items-center gap-1"><Clock size={10} /> {post.readTime}</span>
            <div className="flex items-center gap-1 flex-wrap">
              <Tag size={10} />
              {post.tags.map(t => (
                <span key={t} className="bg-neutral-900 border border-neutral-800 px-2 py-0.5 rounded-sm">{t}</span>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-12">
            {/* Content */}
            <article className="lg:col-span-2">
              <div
                className="prose prose-invert prose-sm max-w-none
                  prose-headings:font-teko prose-headings:uppercase prose-headings:tracking-wide prose-headings:text-white
                  prose-h2:text-3xl prose-h2:mt-8 prose-h2:mb-3
                  prose-h3:text-2xl prose-h3:mt-6 prose-h3:mb-2
                  prose-p:text-neutral-300 prose-p:leading-relaxed prose-p:font-inter prose-p:text-sm
                  prose-strong:text-white prose-strong:font-semibold
                  prose-li:text-neutral-300 prose-li:font-inter prose-li:text-sm
                  prose-a:text-red-500 hover:prose-a:text-red-400
                  prose-ul:space-y-1
                "
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {/* Share / Tags */}
              <div className="mt-10 pt-8 border-t border-neutral-800">
                <div className="flex flex-wrap gap-2 mb-6">
                  {post.tags.map(t => (
                    <span key={t} className="text-xs text-neutral-400 border border-neutral-700 px-3 py-1 font-inter">{t}</span>
                  ))}
                </div>
                <div className="bg-red-900/20 border border-red-900/40 p-6">
                  <h3 className="font-teko text-2xl text-white uppercase mb-2">Ready to Get Started?</h3>
                  <p className="text-neutral-400 font-inter text-sm mb-4">Get a free quote from Ninja Screen Print — Dallas's premier custom apparel printer. Response in 2 hours.</p>
                  <Link to="/contact" data-testid="post-cta-btn" className="ninja-btn-primary font-teko tracking-widest px-8 py-3 inline-block">GET A FREE QUOTE</Link>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="space-y-6">
              {/* Services CTA */}
              <div className="ninja-card p-5">
                <div className="red-line" />
                <h3 className="font-teko text-xl text-white uppercase tracking-wide mb-3">Our Services</h3>
                <div className="space-y-2">
                  {["Screen Printing", "DTF Printing", "Embroidery", "Custom Jerseys", "Graphic Design"].map(s => (
                    <Link key={s} to={`/services/${s.toLowerCase().replace(/ /g, "-")}`} className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white transition-colors font-inter group">
                      <ArrowRight size={10} className="text-red-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                      {s}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Quick Quote */}
              <div className="ninja-card p-5 bg-red-700/10 border-red-700/30">
                <h3 className="font-teko text-xl text-white uppercase tracking-wide mb-2">Get a Free Quote</h3>
                <p className="text-neutral-400 font-inter text-xs mb-4">Response in 2 hours. Free DFW delivery on 24+ pieces.</p>
                <Link to="/contact" className="ninja-btn-primary font-teko tracking-widest w-full text-center py-3 text-sm block">GET QUOTE</Link>
                <a href="tel:+12145550190" className="block text-center text-xs text-neutral-500 mt-2 font-inter hover:text-white transition-colors">(214) 555-0190</a>
              </div>

              {/* Related Posts */}
              <div>
                <h3 className="font-teko text-lg text-white uppercase tracking-widest mb-4">Related Articles</h3>
                <div className="space-y-3">
                  {related.map(r => (
                    <Link key={r.slug} to={`/blog/${r.slug}`} className="ninja-card p-4 flex gap-3 group">
                      <img src={r.image} alt={r.title} className="w-16 h-16 object-cover shrink-0" />
                      <div>
                        <div className="text-xs text-red-500 font-inter mb-1">{r.category}</div>
                        <p className="text-xs text-white font-inter group-hover:text-red-400 transition-colors leading-snug line-clamp-2">{r.title}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
}
